import React from "react"
import { GetStaticPaths, GetStaticProps } from "next"
import { Container, Box, Grid } from "@mui/material"
import Icon from "@mdi/react"
import { mdiShieldHalfFull, mdiCertificate, mdiCreditCardMultipleOutline } from "@mdi/js"

import Button from "components/Buttons/Button"
import SEO from "@secureo/common/components/SEO"
import BreadCrumbs, { BreadCrumb } from "components/BreadCrumbs"
import Link from "components/i18n/Link"
import BrandsSlider from "containers/HomePage/BrandsSlider"

import fetchBrandCategories from "@secureo/common/utils/commerceTools/graphql/queries/fetchBrandCategories"
import { anonymousAccessTokenCache } from "@secureo/common/utils/commerceTools/token/accessTokenCache"

import { helpCenterAncestorBreadCrumbs } from "./index"
import { supportedValidLocales } from "@secureo/common/utils/i18n/supportedLocalesAndCurrencyCodes"

import { Category } from "@secureo/common/typings/Category"

const facts: {
	headline: string
	icon: string
	text: string
	linkProps: {
		label: string
		href: string
	}
}[] = [
	{
		headline: "Datenschutz",
		icon: mdiShieldHalfFull,
		text: "Ihre Daten gehören Ihnen und so soll es auch bleiben. Daher werden ihre Daten durch verschiedene Maßnahmen strengstens geschützt und unterliegen der DSGVO.",
		linkProps: {
			label: "Datenschutzinformationen",
			href: "/help/imprint",
		},
	},
	{
		headline: "Höchste Qualität",
		icon: mdiCertificate,
		text: "Die meisten unserer Produkte werden in Österreich entwickelt und produziert. Somit erfüllen wir höchste Qualitätsansprüche.",
		linkProps: {
			label: "Zu unseren Produkten ",
			href: "/brands/secureo",
		},
	},
	{
		headline: "Sichere Zahlung",
		icon: mdiCreditCardMultipleOutline,
		text: "Sie haben die Möglichkeit aus diversen sicheren Zahlungsarten zu wählen. Bei Vorauskasse bekommen Sie -2% Rabatt auf die gesamte Bestellung!",
		linkProps: {
			label: "Zahlungsinformationen ",
			href: "/help/shipping-and-payment",
		},
	},
]

const WhyChooseUsPage = ({ locale, brandCategories = [] }: Props) => {
	const currentPath: BreadCrumb = {
		href: "/help/why-choose-us",
		label: "Warum mit Uns?",
	}

	const asPath = `/${locale}/help/why-choose-us`

	return (
		<>
			<SEO asPath={asPath} appendLocalizedShopNameWithPipe title="Warum mit Uns?" />
			<div style={{ position: "relative", overflow: "hidden" }}>
				<div
					style={{
						backgroundImage:
							"url(https://res.cloudinary.com/digitalzylinder-shop/image/upload/v1590561445/DigiShop2/Content/Half-Circle-Background.png)",
						height: 580,
						position: "absolute",
						marginTop: -100,
						left: 0,
						width: "100%",
						backgroundRepeat: "no-repeat",
					}}
				/>
				<div style={{ position: "relative" }}>
					<BreadCrumbs
						locale={locale}
						currentPath={currentPath}
						ancestors={helpCenterAncestorBreadCrumbs}
					/>
				</div>
				<Container style={{ position: "relative" }}>
					<Box pt={5} mb={5}>
						<Grid container spacing={3}>
							<Grid item xs={12} md={6}>
								<div style={{ maxWidth: 450 }}>
									<div
										style={{
											color: "#4691CE",
											textTransform: "uppercase",
											fontSize: 13,
										}}
									>
										<strong>Warum mit uns?</strong>
									</div>
									<Box mb={2}>
										<h1 style={{ margin: 0 }}>Für das Gefühl von Sicherheit</h1>
									</Box>
									<p style={{ color: "#505e5f" }}>
										Unter diesem Leitspruch ist es unser Bestreben, Ihnen
										hochwertige Sicherheitsprodukte zu leistbaren Preisen
										anzubieten.
									</p>
									<Box mt={2}>
										<Link href="/brands/secureo">
											<Button color="primary">Produkte entdecken</Button>
										</Link>
									</Box>
								</div>
							</Grid>
							<Grid item xs={12} md={6}>
								<img
									alt="warum-mit-uns"
									src="https://res.cloudinary.com/digitalzylinder-shop/image/upload/v1590561067/DigiShop2/Content/Warum-mit-uns.png"
									style={{
										width: "100%",
										height: 300,
										objectFit: "cover",
										borderRadius: 5,
									}}
								/>
							</Grid>
							{facts.map((fact) => {
								const { headline, icon, text, linkProps } = fact

								const { label, href } = linkProps

								return (
									<Grid item xs={12} sm={4} key={headline}>
										<Box mt={5} style={{ display: "flex", maxWidth: 450 }}>
											<Box mr={3}>
												<Icon path={icon} size={1.5} color="#4691CE" />
											</Box>
											<div>
												<Box mb={2} style={{ fontSize: 26 }}>
													<strong>{headline}</strong>
												</Box>
												<p style={{ color: "#505e5f" }}>{text}</p>
												<Box mt={2}>
													<Link href={href}>{label}</Link>
												</Box>
											</div>
										</Box>
									</Grid>
								)
							})}
						</Grid>
					</Box>
					<BrandsSlider brandCategories={brandCategories} />
				</Container>
			</div>
		</>
	)
}

interface Props {
	brandCategories: Category[]
	locale: string
}

export const getStaticPaths: GetStaticPaths = async () => {
	const paths = supportedValidLocales.map((locale) => {
		return { params: { locale } }
	})

	return {
		paths,
		fallback: false,
	}
}

export const getStaticProps: GetStaticProps = async (context) => {
	const accessToken = await anonymousAccessTokenCache()
	const locale = context.params.locale as string

	const brandCategories = await fetchBrandCategories(accessToken, locale)

	const props: Props = {
		brandCategories,
		locale,
	}

	return { props }
}

export default WhyChooseUsPage
